<template>
  <div class="article-view" :style="backgroundStyle">
    <!-- 背景图层 -->
    <div class="background-layer" :style="backgroundLayerStyle"></div>
    
    <el-card class="article-header" :style="contentCardStyle">
      <!-- 按钮组 - 放在右下角 -->
      <div class="header-buttons">
        <!-- 返回按钮 -->
        <el-tooltip content="返回首页" placement="top" :enterable="false">
          <el-button 
            class="icon-btn back-btn" 
            type="primary" 
            size="small" 
            circle
            @click="goBack"
          >
            <i class="el-icon-back"></i>
          </el-button>
        </el-tooltip>
        
        <!-- 背景设置按钮 -->
        <background-settings @background-updated="handleBgSettingsUpdated" />
        
        <!-- 显示目录按钮 -->
        <el-tooltip content="文章目录" placement="top" :enterable="false">
          <el-button 
            class="icon-btn show-toc-btn" 
            type="primary" 
            size="small" 
            circle
            @click="toggleNavigation"
          >
            <i class="el-icon-menu"></i>
          </el-button>
        </el-tooltip>
      </div>
      
      <div class="header-content">
        <div class="title-row">
          <el-tag
            :color="getArticleTypeStyle(article.article_type)"
            effect="dark"
            class="article-type"
          >
            {{ article.article_type }}
          </el-tag>
          <h2 class="article-title">{{ article.article_name }}</h2>
        </div>

        <div class="time-info">
          <span>
            <i class="el-icon-date"></i>
            {{ article.create_time }}
          </span>
          <span class="divider">|</span>
          <template v-if="article.update_time">
            <span class="update-time">
              <i class="el-icon-refresh"></i>
              {{ article.update_time }}
            </span>
            <span class="divider">|</span>
          </template>
          <span class="divider">|</span>
          <div class="article-tags">
            <el-tag
              v-for="tag in articleTags"
              :key="tag"
              size="small"
              class="article-tag"
            >
              {{ tag }}
            </el-tag>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 内容区域容器 -->
    <div class="content-container">
      <!-- 文章内容区域 -->
      <div class="main-content-wrapper">
        <!-- 文章内容 -->
        <el-card class="article-content" :class="{ 'no-navigation': !showNavigation }" :style="contentCardStyle">
          <!-- 自定义渲染容器 -->
          <div class="custom-markdown-container">
            <div
              ref="markdownDisplay"
              class="markdown-display"
              v-html="renderedContent"
              @click="handleContentClick"
            ></div>
          </div>
        </el-card>
        
        <!-- 导航区域 - 独立卡片 -->
        <div v-show="showNavigation" class="navigation-card" :style="navigationCardStyle">
          <div class="navigation-header">
            <div class="title-wrapper" @mouseenter="showTitleTooltip = true" @mouseleave="showTitleTooltip = false">
              <span>文章目录</span>
              <div v-show="showTitleTooltip" class="custom-tooltip">文章目录</div>
            </div>
            <el-tooltip content="关闭目录" placement="top" :enterable="false">
              <i class="el-icon-close" @click="toggleNavigation"></i>
            </el-tooltip>
          </div>
          <div class="navigation-content">
            <div 
              v-for="(item, index) in navigationItems" 
              :key="index"
              class="navigation-item"
              :class="{ active: currentHeadingId === item.id }"
              :style="{ paddingLeft: (item.level - 1) * 15 + 20 + 'px' }"
              @click="scrollToHeading(item.id)"
            >
              {{ item.text }}
            </div>
            <div v-if="navigationItems.length === 0" class="navigation-empty">
              无目录内容
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mavonEditor } from 'mavon-editor'
import 'mavon-editor/dist/css/index.css'
import * as marked from 'marked'
import BackgroundSettings from '@/components/BackgroundSettings.vue'

export default {
  name: 'ArticleView',
  components: {
    mavonEditor,
    BackgroundSettings
  },
  data() {
    return {
      article: {
        article_name: '',
        article_type: '',
        article_info: '',
        article_tag: '',
        create_time: '',
        update_time: ''
      },
      showNavigation: true,
      navigationItems: [],
      renderedContent: '',
      currentHeadingId: '',
      scrollObserver: null,
      showTitleTooltip: false,
      // 背景相关设置
      contentCardStyle: { backgroundColor: 'rgba(255, 255, 255, 0.8)' },
      backgroundLayerStyle: {},
      backgroundStyle: { backgroundColor: '#f0f2f5' },
      navigationCardStyle: { backgroundColor: 'rgba(255, 255, 255, 0.8)' }
    }
  },
  computed: {
    articleTags() {
      return this.article.article_tag ? this.article.article_tag.split(',') : []
    }
  },
  watch: {
    // 监听路由变化，重新加载背景设置
    '$route'(to, from) {
      if (to.params.articleCode !== from.params.articleCode) {
        // 如果文章ID变化，加载全局设置
        this.loadBgSettings();
      }
    }
  },
  created() {
    // 加载文章
    if (this.$route.params.articleCode) {
      this.getDetail(this.$route.params.articleCode)
    }
  },
  mounted() {
    // 加载必要的脚本
    this.loadMathJax()
    
    // 初始化滚动监听
    this.initScrollObserver()
    
    // 添加全局样式，确保行内代码样式正确显示
    this.addGlobalStyles()
    
    // 监听storage变化，以便在其他页面修改设置时同步更新
    window.addEventListener('storage', this.handleStorageChange);
  },
  beforeDestroy() {
    // 清理滚动监听
    if (this.scrollObserver) {
      this.scrollObserver.disconnect()
    }
    
    // 移除全局样式
    this.removeGlobalStyles()
    
    // 移除storage事件监听
    window.removeEventListener('storage', this.handleStorageChange);
  },
  methods: {
    // 添加全局样式
    addGlobalStyles() {
      const styleEl = document.createElement('style')
      styleEl.id = 'article-global-styles'
      styleEl.textContent = `
        .markdown-display p code {
          padding: 0.2em 0.4em;
          margin: 0 0.2em;
          background-color: #f6f8fa;
          color: #476582;
          border-radius: 3px;
          font-size: 0.9em;
          font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
          white-space: nowrap;
        }

        .markdown-display li code {
          padding: 0.2em 0.4em;
          margin: 0 0.2em;
          background-color: #f6f8fa;
          color: #476582;
          border-radius: 3px;
          font-size: 0.9em;
          font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
          white-space: nowrap;
        }

        /* 彩色行间公式特殊处理 - 确保与普通公式一致 */
        .display-math-colored {
          display: block;
          text-align: center;
          width: 100%;
          margin: 1em 0;
          padding: 0.5em 0;
          /* 与普通公式保持一致的样式 */
          background: transparent;
          border-radius: 0;
          border-bottom: none;
        }
        
        /* 暗色模式下的彩色公式 - 也移除背景色 */
        @media (prefers-color-scheme: dark) {
          .display-math-colored {
            /*background-color: #1e1e1e;*/
          }
        }
        
        .display-math-colored .MathJax_SVG_Display,
        .display-math-colored .MathJax_Display {
          margin: 0 !important;
          padding: 0 !important;
          background: transparent !important;
          box-shadow: none !important;
        }
        
        .markdown-display pre {
          background-color: #0d1117;
          padding: 0;
          border-radius: 6px;
          overflow: hidden;
          position: relative;
          width: 90%;
          max-width: 900px;
          margin: 16px 0;
          border: 1px solid #30363d;
          display: flex;
          flex-direction: column;
        }
        
        .markdown-display pre code {
          color: #c9d1d9;
          font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
          font-size: 14px;
          line-height: 1.5;
          display: block;
          padding: 16px 16px 16px 16px;
          overflow-x: auto;
          tab-size: 4;
          white-space: pre;
        }
        
        /* 代码容器样式 */
        .markdown-display pre .code-container {
          flex: 1;
          overflow-x: auto;
          padding-left: 0; /* 移除左侧内边距 */
          position: relative;
        }
        
        /* 行号样式 */
        .markdown-display pre .line-numbers {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          padding: 16px 8px;
          color: #6e7681;
          text-align: right;
          background-color: #161b22;
          border-right: 1px solid #30363d;
          user-select: none;
          font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
          white-space: pre;
          line-height: 1.5;
          font-size: 14px;
          width: 40px; /* 固定宽度 */
          box-sizing: border-box;
          z-index: 1;
        }
        
        .markdown-display pre .copy-code-btn {
          position: absolute;
          top: 8px;
          right: 8px;
          padding: 4px 8px;
          background: rgba(110, 118, 129, 0.4);
          color: #c9d1d9;
          border: none;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.2s;
          z-index: 10;
        }
        
        .markdown-display pre:hover .copy-code-btn {
          opacity: 1;
        }
        
        .markdown-display pre .copy-code-btn:hover {
          background: rgba(110, 118, 129, 0.6);
        }
        
        /* 表格样式修复 */
        .markdown-display table {
          border-collapse: collapse;
          width: 100%;
          margin: 16px 0;
          display: block;
          overflow-x: auto;
        }
        
        .markdown-display table th,
        .markdown-display table td {
          padding: 8px 12px;
          border: 1px solid #dfe2e5;
        }
        
        .markdown-display table th {
          background-color: #f6f8fa;
          font-weight: 600;
        }
        
        .markdown-display table tr:nth-child(2n) {
          background-color: #f8f8f8;
        }
        
        /* 自定义highlight.js样式调整 */
        .hljs-keyword {
          color: #ff7b72 !important;
        }
        
        .hljs-built_in {
          color: #79c0ff !important;
        }
        
        .hljs-type {
          color: #ffa657 !important;
        }
        
        .hljs-string {
          color: #a5d6ff !important;
        }
        
        .hljs-number {
          color: #d2a8ff !important;
        }
        
        .hljs-comment {
          color: #8b949e !important;
          font-style: italic !important;
        }
        
        .hljs-function {
          color: #d2a8ff !important;
        }
        
        .hljs-title {
          color: #79c0ff !important;
        }
        
        .hljs-title.function_ {
          color: #d2a8ff !important;
        }
        
        .hljs-title.class_ {
          color: #ffa657 !important;
        }
        
        .hljs-params {
          color: #c9d1d9 !important;
        }
        
        .hljs-operator {
          color: #ff7b72 !important;
        }
        
        .hljs-punctuation {
          color: #c9d1d9 !important;
        }
        
        .hljs-property {
          color: #79c0ff !important;
        }
        
        .hljs-tag {
          color: #7ee787 !important;
        }
        
        .hljs-attr {
          color: #d2a8ff !important;
        }
        
        .hljs-name {
          color: #7ee787 !important;
        }

        .hljs-variable {
          color: #ffa657 !important;
        }
        
        .hljs-template-variable {
          color: #ffa657 !important;
        }
        
        .hljs-literal {
          color: #0550ae !important;
        }
        
        .hljs-meta {
          color: #8b949e !important;
        }
        
        .hljs-selector {
          color: #d2a8ff !important;
        }
        
        .hljs-char {
          color: #a5d6ff !important;
        }
        
        .hljs-doctag {
          color: #8b949e !important;
          font-style: italic !important;
        }
        
        .hljs-addition {
          color: #aff5b4 !important;
          background-color: #033a16 !important;
        }
        
        .hljs-deletion {
          color: #ffdcd7 !important;
          background-color: #67060c !important;
        }
        
        .hljs-emphasis {
          font-style: italic !important;
        }
        
        .hljs-strong {
          font-weight: bold !important;
        }
        
        /* 数学公式样式 */
        .markdown-display .MathJax_Display,
        .markdown-display .MathJax_SVG_Display,
        .markdown-display .mjx-chtml.MJXc-display {
          overflow-x: auto !important;
          overflow-y: hidden !important;
          margin: 1em 0 !important;
          padding: 0.5em 0 !important;
          background: transparent !important; /* 移除背景色 */
          border-radius: 0 !important; /* 移除边框圆角 */
          box-shadow: none !important; /* 移除阴影 */
          text-align: center !important;
          display: block !important;
          width: 100% !important;
          position: relative !important;
          border-bottom: none !important; /* 移除底部横线 */
        }
        
        /* 实际公式元素 */
        .markdown-display .MathJax_Display .MathJax,
        .markdown-display .MathJax_SVG_Display .MathJax_SVG {
          cursor: pointer !important;
          display: inline-block !important;
          margin: 0 auto !important;
        }
        
        /* 改进行间公式容器样式 */
        .MathJax_SVG_Display, .MathJax_Display {
          display: block !important;
          text-align: center !important;
          margin: 1em 0 !important;
          background: transparent !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          border-bottom: none !important;
        }
        
        /* 彩色行间公式样式 - 特殊处理 */
        .MathJax_SVG[style*="color:red"], .MathJax[style*="color:red"],
        .MathJax_SVG[style*="color:blue"], .MathJax[style*="color:blue"],
        .MathJax_SVG[style*="color:green"], .MathJax[style*="color:green"],
        .MathJax_SVG[style*="color:purple"], .MathJax[style*="color:purple"],
        .MathJax_SVG[style*="color:orange"], .MathJax[style*="color:orange"] {
          display: inline-block !important;
          margin: 0 auto !important;
        }
        
        /* 强制行间公式容器样式 */
        .markdown-display .MathJax_SVG_Display, .markdown-display .MathJax_Display {
          display: block !important;
          text-align: center !important;
          width: 100% !important;
          margin: 1em 0 !important;
          position: relative !important;
          background: transparent !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          border-bottom: none !important;
        }
        
        /* 行内公式样式 */
        .markdown-display .MathJax:not(.MathJax_Display),
        .markdown-display .MathJax_SVG:not(.MathJax_SVG_Display),
        .markdown-display .mjx-chtml:not(.MJXc-display) {
          margin: 0 0.15em !important;
          color: #0550ae !important;
          vertical-align: middle !important;
          display: inline-block !important;
        }
        
        /* 暗色模式下的公式样式 */
        @media (prefers-color-scheme: dark) {
          .markdown-display .MathJax:not(.MathJax_Display),
          .markdown-display .MathJax_SVG:not(.MathJax_SVG_Display),
          .markdown-display .mjx-chtml:not(.MJXc-display) {
            color: #58a6ff !important;
          }
          
          /* 暗色模式下的代码高亮 - 对C++特别优化 */
          .hljs-keyword {
            color: #ff7b72 !important;
          }
          
          .hljs-built_in {
            color: #79c0ff !important;
          }
          
          .hljs-type {
            color: #ffa657 !important;
          }
          
          .hljs-string {
            color: #a5d6ff !important;
          }
          
          .hljs-number {
            color: #d2a8ff !important;
          }
          
          .hljs-comment {
            color: #8b949e !important;
            font-style: italic !important;
          }
          
          .hljs-function {
            color: #d2a8ff !important;
          }
          
          .hljs-title {
            color: #79c0ff !important;
          }
          
          .hljs-title.function_ {
            color: #d2a8ff !important;
          }
          
          .hljs-title.class_ {
            color: #ffa657 !important;
          }
          
          .hljs-params {
            color: #c9d1d9 !important;
          }
          
          .hljs-operator {
            color: #ff7b72 !important;
          }
          
          .hljs-punctuation {
            color: #c9d1d9 !important;
          }
          
          .hljs-property {
            color: #79c0ff !important;
          }
          
          .hljs-variable {
            color: #ffa657 !important;
          }
          
          .hljs-meta {
            color: #8b949e !important;
          }
          
          .hljs-preprocessor {
            color: #ff7b72 !important;
          }
          
          .hljs-directive {
            color: #ff7b72 !important;
          }
          
          /* 针对C++中的一些特殊关键词 */
          pre code [class*="hljs-keyword"] {
            color: #ff7b72 !important;
          }
          
          /* C++预处理器指令 */
          pre code [class*="hljs-meta"] {
            color: #ff7b72 !important;
          }
          
          /* 针对C++中的特殊标识符 */
          pre code #include, pre code #define, pre code #pragma {
            color: #ff7b72 !important;
          }
          
          /* 增强C++头文件显示 */
          pre code .hljs-string {
            color: #a5d6ff !important;
          }
        }
        
        /* MathJax公式滚动条样式 */
        .markdown-display .MathJax_SVG_Display::-webkit-scrollbar {
          height: 4px;
        }
        
        .markdown-display .MathJax_SVG_Display::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.2);
          border-radius: 2px;
        }

        /* 自定义容器样式 */
        .custom-container {
          margin: 16px 0;
          border-radius: 6px;
          overflow: hidden;
          border: 1px solid;
        }
        .container-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          cursor: pointer;
          user-select: none;
        }
        .container-icon { font-size: 16px; }
        .container-title { font-weight: 600; font-size: 14px; color: #303133; flex: 1; }
        .container-toggle { font-size: 12px; color: #909399; transition: transform 0.2s; }
        .custom-container.collapsed .container-content { display: none; }
        .custom-container:not(.collapsed) .container-toggle { transform: rotate(90deg); }
        .container-content {
          padding: 0 14px 12px;
          font-size: 14px;
          line-height: 1.7;
          color: #2c3e50;
        }
        @media (prefers-color-scheme: dark) {
          .container-title { color: #e0e0e0; }
          .container-content { color: #c9d1d9; }
        }
      `
      document.head.appendChild(styleEl)
    },
    
    // 移除全局样式
    removeGlobalStyles() {
      const styleEl = document.getElementById('article-global-styles')
      if (styleEl) {
        styleEl.parentNode.removeChild(styleEl)
      }
    },
    
    // 加载MathJax
    loadMathJax() {
      // 清除可能存在的旧MathJax实例
      if (window.MathJax) {
        window.MathJax = null;
      }
      
      // 加载MathJax配置
      const mathjaxConfig = document.createElement('script');
      mathjaxConfig.type = 'text/x-mathjax-config';
      mathjaxConfig.textContent = `
        MathJax.Hub.Config({
          tex2jax: {
            inlineMath: [['$','$']],
            displayMath: [['$$','$$']],
            processEscapes: true,
            skipTags: ['script', 'noscript', 'style', 'textarea', 'pre']
          },
          messageStyle: 'none',
          showProcessingMessages: false,
          jax: ["input/TeX", "output/SVG"],
          extensions: ["tex2jax.js", "MathZoom.js", "MathMenu.js", "toMathML.js", "TeX/noErrors.js", "TeX/noUndefined.js", "TeX/AMSmath.js", "TeX/AMSsymbols.js", "TeX/color.js"],
          TeX: { 
            extensions: ["AMSmath.js", "AMSsymbols.js", "color.js"],
            Macros: {
              red: ["{\\\\color{red}#1}", 1],
              blue: ["{\\\\color{blue}#1}", 1],
              green: ["{\\\\color{green}#1}", 1],
              purple: ["{\\\\color{purple}#1}", 1],
              orange: ["{\\\\color{orange}#1}", 1]
            }
          },
          SVG: {
            scale: 110,
            linebreaks: { automatic: true }
          },
          "HTML-CSS": {
            availableFonts: ["TeX"],
            scale: 110
          },
          // 禁用点击缩放功能，避免与自定义缩放功能冲突
          menuSettings: {
            zoom: "None",
            showRenderer: false,
            showLocale: false,
            showFontMenu: false
          },
          MathMenu: {
            showRenderer: false,
            showLocale: false,
            showFontMenu: false
          },
          MathZoom: {
            delay: 600,
            styles: {
              "#MathJax_Zoom": {
                "background-color": "#FFFFFF",
                "box-shadow": "0px 0px 15px #888888",
                "border": "1px solid #CCCCCC",
                "padding": "10px",
                "max-width": "90%",
                "margin": "0 auto",
                "z-index": "9999",
                "position": "fixed",
                "left": "0",
                "right": "0",
                "top": "50px",
                "overflow": "auto",
                "max-height": "80vh"
              }
            }
          }
        });
        
        // 添加特殊处理，确保行间公式正确居中显示
        MathJax.Hub.Register.StartupHook("End", function () {
          // 找到所有行间公式容器
          const displayMathContainers = document.querySelectorAll('.MathJax_SVG_Display, .MathJax_Display');
          displayMathContainers.forEach(container => {
            // 应用居中样式
            container.style.display = 'block';
            container.style.textAlign = 'center';
            
            // 确保位于单独行
            const parentEl = container.parentNode;
            if (parentEl && parentEl.tagName.toLowerCase() !== 'div' && parentEl.tagName.toLowerCase() !== 'p') {
              // 如果公式不在段落或块级元素内，则包装它
              const wrapper = document.createElement('div');
              wrapper.style.display = 'block';
              wrapper.style.textAlign = 'center';
              wrapper.style.margin = '1em 0';
              parentEl.insertBefore(wrapper, container);
              wrapper.appendChild(container);
            }
          });
        });
      `;
      
      document.head.appendChild(mathjaxConfig);
      
      // 直接从CDN加载MathJax
      const cdnScript = document.createElement('script');
      cdnScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.9/MathJax.js?config=TeX-AMS_SVG';
      cdnScript.async = true;
      cdnScript.crossOrigin = "anonymous";
      
      cdnScript.onload = () => {
        console.log('CDN MathJax加载成功');
        this.initMathJax();
      };
      
      cdnScript.onerror = (err) => {
        console.error('主要CDN加载失败，尝试备用CDN', err);
        
        // 尝试备用CDN
        const backupScript = document.createElement('script');
        backupScript.src = 'https://cdn.jsdelivr.net/npm/mathjax@2.7.9/MathJax.js?config=TeX-AMS_SVG';
        backupScript.async = true;
        backupScript.crossOrigin = "anonymous";
        
        backupScript.onload = () => {
          console.log('备用CDN MathJax加载成功');
          this.initMathJax();
        };
        
        backupScript.onerror = (backupErr) => {
          console.error('所有MathJax加载尝试均失败', backupErr);
          alert('加载数学公式渲染引擎失败，请刷新页面重试');
        };
        
        document.head.appendChild(backupScript);
      };
      
      document.head.appendChild(cdnScript);
      
      // 监听MathJax渲染完成事件，只在这里初始化代码块
      document.addEventListener('mathjax-rendered', () => {
        console.log('MathJax渲染完成事件触发，初始化代码块');
        this.initCodeBlocks();
      }, { once: true });
    },
    
    // 初始化MathJax并渲染公式
    initMathJax() {
      if (window.MathJax) {
        setTimeout(() => {
          try {
            console.log('开始渲染LaTeX公式');
            
            // 确保MathJax完全加载
            if (!window.MathJax.Hub) {
              console.warn('MathJax.Hub不可用，等待MathJax完全加载');
              setTimeout(this.initMathJax.bind(this), 500);
              return;
            }
            
            // 确保SVG输出器已加载
            if (!window.MathJax.OutputJax.SVG) {
              console.warn('MathJax SVG输出器未加载，等待加载完成');
              setTimeout(this.initMathJax.bind(this), 500);
              return;
            }
            
            // 清理可能存在的错误状态
            const elements = document.querySelectorAll('.MathJax_Error');
            elements.forEach(el => el.parentNode.removeChild(el));
            
            // 开始处理公式
            window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub, this.$refs.markdownDisplay]);
            
            // 处理颜色公式（使用安全的方式添加回调）
            const self = this;
            window.MathJax.Hub.Queue(function() {
              try {
                self.enhanceMathColors();
              } catch (e) {
                console.error('增强颜色处理失败:', e);
              }
              
              // 触发渲染完成事件
              try {
                const event = new CustomEvent('mathjax-rendered');
                document.dispatchEvent(event);
              } catch (e) {
                console.error('触发事件失败:', e);
              }
            });
          } catch (e) {
            console.error('MathJax渲染失败', e);
          }
        }, 500);
      } else {
        console.warn('MathJax对象不可用');
        // 等待500ms后重试
        setTimeout(this.initMathJax.bind(this), 500);
      }
    },
    
    // 加载highlight.js
    loadHighlightJs() {
      return new Promise((resolve, reject) => {
        // 如果已经加载，直接返回
        if (window.hljs) {
          resolve(window.hljs)
          return
        }
        
        // 加载highlight.js CSS - 更换为更好看的主题
        const cssLink = document.createElement('link')
        cssLink.rel = 'stylesheet'
        cssLink.href = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.7.0/styles/github-dark.min.css'
        document.head.appendChild(cssLink)
        
        // 加载highlight.js JS
        const script = document.createElement('script')
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.7.0/highlight.min.js'
        script.async = true
        
        script.onload = () => {
          // 加载C++语言包
          const cppScript = document.createElement('script')
          cppScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.7.0/languages/cpp.min.js'
          cppScript.async = true
          
          cppScript.onload = () => {
            resolve(window.hljs)
          }
          
          cppScript.onerror = (err) => {
            console.error('加载C++语言包失败:', err)
            // 即使C++语言包加载失败，仍然可以使用基本的highlight.js
            resolve(window.hljs)
          }
          
          document.head.appendChild(cppScript)
        }
        
        script.onerror = (err) => {
          console.error('加载highlight.js失败:', err)
          reject(err)
        }
        
        document.head.appendChild(script)
      })
    },
    
    // 初始化滚动监听
    initScrollObserver() {
      // 使用Intersection Observer API监听标题元素
      if ('IntersectionObserver' in window) {
        this.scrollObserver = new IntersectionObserver(
          (entries) => {
            const visibleHeadings = entries
              .filter(entry => entry.isIntersecting)
              .map(entry => entry.target)
              .sort((a, b) => a.offsetTop - b.offsetTop)
            
            if (visibleHeadings.length > 0) {
              // 更新当前可见标题
              this.currentHeadingId = visibleHeadings[0].id
            }
          },
          {
            rootMargin: '-100px 0px -80% 0px', // 标题进入视窗顶部100px后触发
            threshold: 0
          }
        )
      }
    },
    
    // 渲染Markdown内容
    renderMarkdown(content) {
      try {
        console.log('开始渲染Markdown内容');
        
        // 预处理自定义容器
        content = this.preprocessCustomContainers(content);
        // 预处理Markdown中的LaTeX颜色命令
        content = this.preprocessLatexColors(content);
        
        // 处理带颜色的行间公式 - 特殊处理
        content = content.replace(/\$\$\\red\{([^}]*)\}\$\$/g, '\n<div class="display-math-colored">\n$$\\color{red}{$1}$$\n</div>\n');
        content = content.replace(/\$\$\\blue\{([^}]*)\}\$\$/g, '\n<div class="display-math-colored">\n$$\\color{blue}{$1}$$\n</div>\n');
        content = content.replace(/\$\$\\green\{([^}]*)\}\$\$/g, '\n<div class="display-math-colored">\n$$\\color{green}{$1}$$\n</div>\n');
        content = content.replace(/\$\$\\purple\{([^}]*)\}\$\$/g, '\n<div class="display-math-colored">\n$$\\color{purple}{$1}$$\n</div>\n');
        content = content.replace(/\$\$\\orange\{([^}]*)\}\$\$/g, '\n<div class="display-math-colored">\n$$\\color{orange}{$1}$$\n</div>\n');
        content = content.replace(/\$\$\\color\{(\w+)\}\{([^}]*)\}\$\$/g, '\n<div class="display-math-colored">\n$$\\color{$1}{$2}$$\n</div>\n');
        
        // 临时保护LaTeX公式
        const mathExpressions = [];
        let protectedContent = content.replace(/(\$\$[\s\S]*?\$\$|\$[^\$\n]+?\$)/g, function(match) {
          console.log("保护LaTeX公式:", match.substring(0, 20) + (match.length > 20 ? "..." : ""));
          mathExpressions.push(match);
          return `MATH_PLACEHOLDER_${mathExpressions.length - 1}`;
        });
        
        // 配置marked
        const renderer = new marked.Renderer();
        
        // 自定义代码渲染 - 简单返回代码，后续由highlight.js处理
        renderer.code = function(code, language) {
          // 确保HTML标签被转义
          code = code.replace(/</g, '&lt;').replace(/>/g, '&gt;');
          return `<pre><code class="language-${language || 'plaintext'}">${code}</code></pre>`;
        };
        
        // 自定义引用渲染
        renderer.blockquote = function(quote) {
          // 移除<p>标签前后的空格，以避免样式问题
          return `<blockquote class="custom-blockquote">${quote.trim()}</blockquote>`;
        };
        
        // 自定义列表渲染 - 处理任务列表
        renderer.list = function(body, ordered, start) {
          // 检查是否包含任务列表项
          const hasTaskItems = body.includes('input[type="checkbox"]');
          const listType = ordered ? 'ol' : 'ul';
          const startAttr = ordered && start !== 1 ? ` start="${start}"` : '';
          
          if (hasTaskItems) {
            // 为任务列表添加特殊类
            return `<${listType} class="task-list"${startAttr}>\n${body}</${listType}>\n`;
          } else {
            // 普通列表
            return `<${listType}${startAttr}>\n${body}</${listType}>\n`;
          }
        };
        
        // 自定义列表项渲染 - 处理任务列表项
        renderer.listitem = function(text) {
          // 移除可能被包装的<p>标签，避免额外的margin
          text = text.replace(/^<p>(.*?)<\/p>$/s, '$1');
          
          // 检查是否为任务列表项
          if (text.includes('input[type="checkbox"]')) {
            return `<li class="task-list-item">${text}</li>\n`;
          } else {
            return `<li>${text}</li>\n`;
          }
        };
        
        marked.setOptions({
          renderer: renderer,
          breaks: true,
          gfm: true,
          headerIds: true,
          langPrefix: 'language-'
        });
        
        // 扩展marked以支持任务列表
        const originalLexer = marked.Lexer.lex;
        marked.Lexer.lex = function(src, options) {
          // 预处理任务列表语法
          src = src.replace(/^- \[([ x])\] (.+)$/gm, function(match, checked, text) {
            const checkbox = checked === 'x' ? 'checked' : '';
            return `- <input type="checkbox" ${checkbox} disabled> ${text}`;
          });
          
          return originalLexer.call(this, src, options);
        };
        
        // 渲染markdown内容
        let renderedHtml = marked.parse(protectedContent);
        
        // 还原LaTeX公式
        renderedHtml = renderedHtml.replace(/MATH_PLACEHOLDER_(\d+)/g, function(match, index) {
          const formula = mathExpressions[parseInt(index)];
          console.log("还原LaTeX公式:", formula.substring(0, 20) + (formula.length > 20 ? "..." : ""));
          // 确保公式不被HTML转义
          return formula;
        });
        
        this.renderedContent = renderedHtml;
        
        // 在下一个渲染周期处理LaTeX公式和代码高亮
        this.$nextTick(async () => {
          // 先扫描标题生成导航
          this.scanHeadingsAndGenerateNavigation();
          
          // 手动处理引用样式
          this.enhanceBlockquotes();
          
          // 处理任务列表样式
          this.enhanceTaskLists();
          
          // 处理LaTeX公式
          if (window.MathJax && window.MathJax.Hub) {
            console.log('使用已加载的MathJax处理公式');
            window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub, this.$refs.markdownDisplay]);
          } else {
            // 如果MathJax还没有加载完成，等待加载完成后再渲染
            console.log('等待MathJax加载后处理公式');
            
            // 创建一次性事件处理函数
            document.addEventListener('mathjax-rendered', () => {
              if (window.MathJax && window.MathJax.Hub) {
                window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub, this.$refs.markdownDisplay]);
              }
            }, { once: true });
          }
          
          // 加载highlight.js并处理代码高亮
          try {
            await this.loadHighlightJs();
            // 不在这里初始化代码块，而是等待 MathJax 渲染完成事件触发时初始化
            // 这样可以避免代码块被初始化多次
          } catch (err) {
            console.error('代码高亮处理失败:', err);
          }
        });
      } catch (error) {
        console.error('Markdown渲染失败:', error);
        // 降级处理 - 直接显示原始内容
        const plainTextContent = `<pre>${content.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`;
        this.renderedContent = plainTextContent;
      }
    },

    // 预处理自定义容器
    preprocessCustomContainers(content) {
      const icon = { info:'ℹ️', tip:'💡', warning:'⚠️', danger:'❌', details:'▶️' }
      const color = { info:'#409EFF', tip:'#67C23A', warning:'#E6A23C', danger:'#F56C6C', details:'#909399' }
      return content.replace(
        /^:::\s*(\w+)(?:\[([^\]]*)\])?\s*\n([\s\S]*?)^:::/gm,
        (match, type, title, body) => {
          const t = (type || '').toLowerCase()
          const defaultCollapsed = (t === 'info' || t === 'details')
          const collapsed = defaultCollapsed ? ' collapsed' : ''
          const ico = icon[t] || 'ℹ️'
          const col = color[t] || '#409EFF'
          const titleHtml = title ? `<span class="container-title">${title}</span>` : ''
          return `<div class="custom-container${collapsed}" data-type="${t}" style="border-color:${col};background:rgba(${this._hexToRgb(col)},0.08)">` +
            `<div class="container-header">` +
              `<span class="container-icon">${ico}</span>` +
              titleHtml +
              `<span class="container-toggle">▶</span>` +
            `</div>` +
            `<div class="container-content">${body.trim()}</div>` +
            `</div>`
        }
      )
    },
    _hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return result ? `${parseInt(result[1],16)},${parseInt(result[2],16)},${parseInt(result[3],16)}` : '64,158,255'
    },

    // 预处理LaTeX颜色命令
    preprocessLatexColors(content) {
      console.log('预处理LaTeX颜色命令');
      
      // 调试：输出原始内容片段
      if (content.includes('$$\\color') || content.includes('$$\\red')) {
        console.log('发现双美元符号颜色公式:', content.substring(0, 100));
      }
      
      // 处理LaTeX公式中的<>符号，在<>两边添加空格，避免被解析为HTML标签
      content = content.replace(/(\$.*?)([^\\])<([^\\])(.*?\$)/g, '$1$2 < $3$4'); // 单美元符号内的<
      content = content.replace(/(\$.*?)([^\\])>([^\\])(.*?\$)/g, '$1$2 > $3$4'); // 单美元符号内的>
      content = content.replace(/(\$\$.*?)([^\\])<([^\\])(.*?\$\$)/g, '$1$2 < $3$4'); // 双美元符号内的<
      content = content.replace(/(\$\$.*?)([^\\])>([^\\])(.*?\$\$)/g, '$1$2 > $3$4'); // 双美元符号内的>
      
      // 处理没有空格的情况，如$i>j$
      content = content.replace(/(\$[^\$]*?)<([^\$\s]*?\$)/g, '$1 < $2');
      content = content.replace(/(\$[^\$]*?)>([^\$\s]*?\$)/g, '$1 > $2');
      content = content.replace(/(\$\$[^\$]*?)<([^\$\s]*?\$\$)/g, '$1 < $2');
      content = content.replace(/(\$\$[^\$]*?)>([^\$\s]*?\$\$)/g, '$1 > $2');
      
      // 处理单美元符号的情况下的颜色命令
      content = content.replace(/\$\\red\{([^}]*)\}\$/g, '$\\color{red}{$1}$');
      content = content.replace(/\$\\blue\{([^}]*)\}\$/g, '$\\color{blue}{$1}$');
      content = content.replace(/\$\\green\{([^}]*)\}\$/g, '$\\color{green}{$1}$');
      content = content.replace(/\$\\purple\{([^}]*)\}\$/g, '$\\color{purple}{$1}$');
      content = content.replace(/\$\\orange\{([^}]*)\}\$/g, '$\\color{orange}{$1}$');
      
      // 处理不带花括号的命令: $\redXXX$ -> $\color{red}{XXX}$（只处理单美元符号）
      content = content.replace(/\$\\red(\S+)\$/g, '$\\color{red}{$1}$');
      content = content.replace(/\$\\blue(\S+)\$/g, '$\\color{blue}{$1}$');
      content = content.replace(/\$\\green(\S+)\$/g, '$\\color{green}{$1}$');
      content = content.replace(/\$\\purple(\S+)\$/g, '$\\color{purple}{$1}$');
      content = content.replace(/\$\\orange(\S+)\$/g, '$\\color{orange}{$1}$');
      
      return content;
    },
    
    // 增强引用样式
    enhanceBlockquotes() {
      if (!this.$refs.markdownDisplay) return;
      
      const blockquotes = this.$refs.markdownDisplay.querySelectorAll('blockquote');
      blockquotes.forEach(blockquote => {
        // 确保添加自定义类
        blockquote.classList.add('custom-blockquote');
        
        // 添加引号图标
        if (!blockquote.querySelector('.quote-icon')) {
          const quoteIcon = document.createElement('span');
          quoteIcon.className = 'quote-icon';
          quoteIcon.innerHTML = '❝';
          blockquote.insertBefore(quoteIcon, blockquote.firstChild);
        }
      });
    },
    
    // 增强任务列表样式
    enhanceTaskLists() {
      if (!this.$refs.markdownDisplay) return;
      
      // 拆分混合UL：同一UL内既有任务项又有普通项
      const uls = this.$refs.markdownDisplay.querySelectorAll('ul');
      uls.forEach(ul => {
        const lis = Array.from(ul.children).filter(n => n.tagName && n.tagName.toLowerCase() === 'li');
        if (!lis.length) return;
        const taskLis = lis.filter(li => li.querySelector('input[type="checkbox"]'));
        const normalLis = lis.filter(li => !li.querySelector('input[type="checkbox"]'));
        if (taskLis.length && normalLis.length) {
          const taskUl = document.createElement('ul');
          taskUl.className = 'task-list';
          taskUl.style.setProperty('padding-left', '20px', 'important');
          taskUl.style.setProperty('list-style', 'none', 'important');
          taskLis.forEach(li => taskUl.appendChild(li));
          const firstIsTask = lis[0].querySelector('input[type="checkbox"]') != null;
          if (firstIsTask) {
            ul.parentNode.insertBefore(taskUl, ul);
          } else {
            ul.parentNode.insertBefore(taskUl, ul.nextSibling);
          }
          // 普通UL保留PC的40px缩进，保持正常margin
          ul.style.setProperty('padding-left', '40px', 'important');
        }
      });
      
      // 查找所有包含复选框的列表项
      const taskListItems = this.$refs.markdownDisplay.querySelectorAll('ul li:has(input[type="checkbox"])');
      
      taskListItems.forEach(item => {
        // 添加任务列表项类
        item.classList.add('task-list-item');
        
        // 确保复选框样式正确
        const checkbox = item.querySelector('input[type="checkbox"]');
        if (checkbox) {
          checkbox.style.margin = '0 8px 0 0';
          checkbox.style.transform = 'scale(1)';
          checkbox.style.flexShrink = '0';
          checkbox.style.verticalAlign = 'middle';
          checkbox.style.position = 'relative';
          checkbox.style.top = '-1px';
        }
        
        // 设置列表项为flex布局并允许换行（为嵌套子列表换行提供空间）
        item.style.display = 'flex';
        item.style.flexWrap = 'wrap';
        item.style.alignItems = 'center';
        item.style.listStyle = 'none';

        // 直接子段落清零外边距，避免UA默认1em/15px撑开
        const directP = item.querySelector(':scope > p');
        if (directP) {
          directP.style.setProperty('margin-top', '0', 'important');
          directP.style.setProperty('margin-bottom', '0', 'important');
        }
        
        // 若包含子级列表，则确保子级独占一行
        const childLists = item.querySelectorAll(':scope > ul, :scope > ol');
        childLists.forEach(cl => {
          cl.style.display = 'block';
          cl.style.width = '100%';
          cl.style.margin = '4px 0 0 0';
        });
        
        // 隐藏默认的列表标记
        item.style.setProperty('--marker', 'none');
        
        // 确保父级ul有正确的缩进
        const parentUl = item.closest('ul');
        if (parentUl) {
          parentUl.style.paddingLeft = '20px';
          parentUl.style.listStyle = 'none';
        }
      });
      
      // 对于不支持:has()选择器的浏览器，使用备用方法
      const allListItems = this.$refs.markdownDisplay.querySelectorAll('ul li, ol li');
      allListItems.forEach(item => {
        const checkbox = item.querySelector('input[type="checkbox"]');
        if (checkbox) {
          // 这是一个任务列表项
          item.classList.add('task-list-item');
          item.style.display = 'flex';
          item.style.flexWrap = 'wrap';
          item.style.alignItems = 'center';
          item.style.listStyle = 'none';

          // 直接子段落清零外边距
          const directP2 = item.querySelector(':scope > p');
          if (directP2) {
            directP2.style.setProperty('margin-top', '0', 'important');
            directP2.style.setProperty('margin-bottom', '0', 'important');
          }
          
          // 若包含子级列表，则确保子级独占一行
          const childLists2 = item.querySelectorAll(':scope > ul, :scope > ol');
          childLists2.forEach(cl => {
            cl.style.display = 'block';
            cl.style.width = '100%';
            cl.style.margin = '4px 0 0 0';
          });
          
          // 确保复选框样式正确
          checkbox.style.margin = '0 8px 0 0';
          checkbox.style.transform = 'scale(1)';
          checkbox.style.flexShrink = '0';
          checkbox.style.verticalAlign = 'middle';
          checkbox.style.position = 'relative';
          checkbox.style.top = '-1px';
          
          // 确保父级ul有正确的缩进
          const parentUl = item.closest('ul');
          if (parentUl) {
            parentUl.style.paddingLeft = '20px';
            parentUl.style.listStyle = 'none';
          }
        }
      });
      
      // 折叠与列表相邻的段落外边距（一些浏览器/主题会给 p 设置 1em/15px 默认外边距）
      const paragraphs = this.$refs.markdownDisplay.querySelectorAll('p');
      paragraphs.forEach(p => {
        const prev = p.previousElementSibling;
        const next = p.nextElementSibling;
        if (next && (next.tagName.toLowerCase() === 'ul' || next.tagName.toLowerCase() === 'ol')) {
          p.style.setProperty('margin-bottom', '0', 'important');
        }
        if (prev && (prev.tagName.toLowerCase() === 'ul' || prev.tagName.toLowerCase() === 'ol')) {
          p.style.setProperty('margin-top', '0', 'important');
        }
        
        // 检测并处理空段落，避免空行导致的1em间距
        const textContent = p.textContent.trim();
        const hasOnlyBr = p.querySelectorAll('br').length > 0 && p.children.length === p.querySelectorAll('br').length;
        const isEmpty = textContent === '' || hasOnlyBr;
        
        if (isEmpty) {
          p.style.setProperty('margin-top', '0', 'important');
          p.style.setProperty('margin-bottom', '0', 'important');
          p.style.setProperty('margin-block-start', '0', 'important');
          p.style.setProperty('margin-block-end', '0', 'important');
        }
      });
      
      // 强制处理：任何在列表之间的段落都应该清零margin
      const allParagraphs = this.$refs.markdownDisplay.querySelectorAll('p');
      allParagraphs.forEach(p => {
        let prev = p.previousElementSibling;
        let next = p.nextElementSibling;
        
        // 跳过非元素节点
        while (prev && prev.nodeType !== Node.ELEMENT_NODE) {
          prev = prev.previousElementSibling;
        }
        while (next && next.nodeType !== Node.ELEMENT_NODE) {
          next = next.nextElementSibling;
        }
        
        // 如果前后都是列表，强制清零margin
        if (prev && (prev.tagName.toLowerCase() === 'ul' || prev.tagName.toLowerCase() === 'ol') &&
            next && (next.tagName.toLowerCase() === 'ul' || next.tagName.toLowerCase() === 'ol')) {
          p.style.setProperty('margin-top', '0', 'important');
          p.style.setProperty('margin-bottom', '0', 'important');
          p.style.setProperty('margin-block-start', '0', 'important');
          p.style.setProperty('margin-block-end', '0', 'important');
        }
        
        // 如果前面是任务列表，后面是普通列表，特别处理
        if (prev && prev.classList.contains('task-list') &&
            next && next.tagName.toLowerCase() === 'ul' && !next.classList.contains('task-list')) {
          p.style.setProperty('margin-top', '0', 'important');
          p.style.setProperty('margin-bottom', '0', 'important');
          p.style.setProperty('margin-block-start', '0', 'important');
          p.style.setProperty('margin-block-end', '0', 'important');
        }
      });
      
      // 专门强制处理非任务列表中的p标签margin
      const nonTaskListParagraphs = this.$refs.markdownDisplay.querySelectorAll('ul:not(.task-list) li > p');
      nonTaskListParagraphs.forEach(p => {
        p.style.setProperty('margin', '0', 'important');
        p.style.setProperty('margin-top', '0', 'important');
        p.style.setProperty('margin-bottom', '0', 'important');
        p.style.setProperty('margin-block-start', '0', 'important');
        p.style.setProperty('margin-block-end', '0', 'important');
        p.style.setProperty('margin-inline-start', '0', 'important');
        p.style.setProperty('margin-inline-end', '0', 'important');
      });
    },
    
    // 处理内容区域点击事件
    handleContentClick(e) {
      // 如果点击的是链接，阻止默认行为，自行处理
      if (e.target.tagName === 'A' && e.target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault()
        const id = e.target.getAttribute('href').substring(1)
        this.scrollToHeading(id)
      }
    },
    
    // 扫描标题并生成导航
    scanHeadingsAndGenerateNavigation() {
      if (!this.$refs.markdownDisplay) return
      
      // 清空导航项
      this.navigationItems = []
      
      // 查找内容区域中的所有标题元素
      const headings = this.$refs.markdownDisplay.querySelectorAll('h1, h2, h3, h4, h5, h6')
      
      // 处理每个标题
      headings.forEach((heading, index) => {
        // 获取标题级别和文本
        const level = parseInt(heading.tagName.substring(1))
        const text = heading.textContent.trim()
        
        // 为标题添加唯一ID
        const id = heading.id || `heading-${index}`
        heading.id = id
        
        // 添加到导航项
        this.navigationItems.push({
          id: id,
          level: level,
          text: text
        })
        
        // 添加到滚动监听
        if (this.scrollObserver) {
          this.scrollObserver.observe(heading)
        }
      })
    },
    
    // 初始化代码块
    initCodeBlocks() {
      if (!this.$refs.markdownDisplay || !window.hljs) return;
      
      const codeBlocks = this.$refs.markdownDisplay.querySelectorAll('pre code');
      
      codeBlocks.forEach((codeEl, index) => {
        const pre = codeEl.parentElement;
        
        // 避免重复初始化
        if (pre.classList.contains('enhanced')) {
          console.log('代码块已增强，跳过');
          return;
        }
        
        // 标记为已增强
        pre.classList.add('enhanced');
        
        // 获取原始代码内容
        const originalCode = codeEl.textContent;
        
        // 使用highlight.js进行高亮
        try {
          // 检测语言
          const languageClass = Array.from(codeEl.classList).find(cls => cls.startsWith('language-'));
          const language = languageClass ? languageClass.replace('language-', '') : '';
          
          // 高亮代码
          if (language && window.hljs.getLanguage(language)) {
            const result = window.hljs.highlight(originalCode, { language });
            codeEl.innerHTML = result.value;
            
            // 对C++代码做特殊处理
            if (language === 'cpp' || language === 'c') {
              this.enhanceCppSyntax(codeEl);
            }
          } else {
            window.hljs.highlightElement(codeEl);
          }
        } catch (err) {
          console.error('代码高亮失败:', err);
          // 失败时保持原始内容，但确保HTML标签被转义
          codeEl.textContent = originalCode;
        }
        
        // 添加行号
        const lines = codeEl.innerHTML.split('\n');
        if (lines.length > 0) {
          const lineNumbersEl = document.createElement('div');
          lineNumbersEl.className = 'line-numbers';
          
          // 生成行号
          const lineNumbers = Array.from(
            { length: lines.length },
            (_, i) => String(i + 1).padStart(2)
          ).join('\n');
          
          lineNumbersEl.textContent = lineNumbers;
          pre.appendChild(lineNumbersEl);
         
          // 添加代码容器，确保代码内容不被行号遮盖
          const codeContainer = document.createElement('div');
          codeContainer.className = 'code-container';
          
          // 移动代码到新容器
          pre.appendChild(codeContainer);
          codeContainer.appendChild(codeEl);
         
          // 设置代码左侧边距，让代码靠左但不被行号遮盖
          codeEl.style.marginLeft = '0';
          codeEl.style.paddingLeft = '0';
          codeContainer.style.marginLeft = '50px';
        }
        
        // 添加复制按钮
        const copyBtn = document.createElement('button');
        copyBtn.className = 'copy-code-btn';
        copyBtn.textContent = '复制';
        
        copyBtn.addEventListener('click', async (e) => {
          e.stopPropagation();
          
          try {
            // 尝试使用现代API
            await navigator.clipboard.writeText(originalCode);
            copyBtn.textContent = '已复制';
            
            this.$message({
              message: '复制成功',
              type: 'success',
              duration: 2000
            });
            
            setTimeout(() => {
              copyBtn.textContent = '复制';
            }, 2000);
          } catch (err) {
            console.error('现代API复制失败，尝试备用方法:', err);
            
            // 备用方法：创建临时textarea元素
            try {
              const textarea = document.createElement('textarea');
              textarea.value = originalCode;
              textarea.style.position = 'fixed';
              textarea.style.opacity = '0';
              document.body.appendChild(textarea);
              textarea.select();
              
              const successful = document.execCommand('copy');
              document.body.removeChild(textarea);
              
              if (successful) {
                copyBtn.textContent = '已复制';
                this.$message({
                  message: '复制成功',
                  type: 'success',
                  duration: 2000
                });
                
                setTimeout(() => {
                  copyBtn.textContent = '复制';
                }, 2000);
              } else {
                throw new Error('execCommand复制失败');
              }
            } catch (fallbackErr) {
              console.error('备用复制方法也失败:', fallbackErr);
              
              this.$message({
                message: '复制失败，请手动选择并复制代码',
                type: 'error',
                duration: 2000
              });
            }
          }
        });
        
        pre.appendChild(copyBtn);
      });
    },
    
    // 增强C++语法高亮
    enhanceCppSyntax(codeElement) {
      try {
        // 查找所有预处理器指令并添加特殊样式
        const content = codeElement.innerHTML;
        
        // 增强预处理器指令
        let enhancedContent = content.replace(
          /(&lt;[a-zA-Z0-9_.\/]+&gt;)/g, 
          '<span class="cpp-include-path">$1</span>'
        );
        
        // 增强宏定义和常数
        enhancedContent = enhancedContent.replace(
          /\b(#include|#define|#pragma|#ifdef|#ifndef|#endif|#if|#else|#elif)\b/g,
          '<span class="cpp-preprocessor">$1</span>'
        );
        
        codeElement.innerHTML = enhancedContent;
        
        // 添加自定义样式
        const styleEl = document.getElementById('cpp-syntax-style') || document.createElement('style');
        if (!styleEl.id) {
          styleEl.id = 'cpp-syntax-style';
          styleEl.textContent = `
            .cpp-include-path {
              color: #a5d6ff !important;
            }
            .cpp-preprocessor {
              color: #ff7b72 !important;
              font-weight: bold !important;
            }
            .language-cpp .hljs-meta {
              color: #ff7b72 !important;
            }
            .language-cpp .hljs-keyword {
              color: #ff7b72 !important;
            }
            .language-cpp .hljs-type {
              color: #ffa657 !important;
            }
            .language-cpp .hljs-number {
              color: #d2a8ff !important;
            }
            .language-cpp .hljs-comment {
              color: #8b949e !important;
              font-style: italic !important;
            }
            .language-cpp .hljs-string {
              color: #a5d6ff !important;
            }
            .language-cpp .hljs-built_in {
              color: #79c0ff !important;
            }
            .language-cpp .hljs-title.function_ {
              color: #d2a8ff !important;
            }
            .language-cpp .hljs-operator {
              color: #ff7b72 !important;
            }
            .language-cpp .hljs-punctuation {
              color: #c9d1d9 !important;
            }
          `;
          document.head.appendChild(styleEl);
        }
      } catch (e) {
        console.error('增强C++语法失败:', e);
      }
    },
    
    // 滚动到指定标题
    scrollToHeading(id) {
      const heading = document.getElementById(id)
      
      if (heading) {
        // 更新当前标题ID
        this.currentHeadingId = id
        
        // 平滑滚动到标题位置
        heading.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        })
      }
    },
    
    // 切换导航显示/隐藏
    toggleNavigation() {
      this.showNavigation = !this.showNavigation
      
      // 如果是第一次显示导航，确保已经生成了导航
      if (this.showNavigation && this.navigationItems.length === 0) {
        this.scanHeadingsAndGenerateNavigation()
      }
    },
    
    // 获取文章类型对应的样式
    getArticleTypeStyle(type) {
      const styleMap = {
        'OI': '#409EFF',
        '题解': '#67C23A',
        '游记': '#E6A23C',
        '生活': '#F56C6C',
        '学习': '#8E44AD',
        '科技': '#16A085',
        '情感': '#D35400',
        '游戏': '#2980B9',
        '随笔': '#27AE60',
        '其他': '#C0392B'
      }
      return styleMap[type] || ''
    },
    
    // 处理背景设置更新事件
    handleBgSettingsUpdated(settings) {
      this.contentCardStyle = settings.contentCardStyle
      this.backgroundLayerStyle = settings.backgroundLayerStyle
      this.backgroundStyle = settings.backgroundStyle
      this.navigationCardStyle = settings.contentCardStyle
    },
    
    // 处理storage事件，当localStorage变化时更新设置
    handleStorageChange(event) {
      if (event.key === 'globalBgSettings') {
        try {
          const settings = JSON.parse(event.newValue);
          if (settings) {
            // 使用相同的逻辑处理值
            this.appliedBgIndex = settings.bgIndex !== undefined ? settings.bgIndex : -1;
            this.appliedContentTransparency = settings.contentTransparency !== undefined ? 
                                             settings.contentTransparency : 0.2;
            this.appliedBgTransparency = settings.bgTransparency !== undefined ? 
                                       settings.bgTransparency : 1;
          }
        } catch (e) {
          console.error('同步背景设置失败:', e);
        }
      }
    },
    
    // 从本地存储加载背景设置
    loadBgSettings() {
      try {
        // 尝试获取全局设置
        const settings = JSON.parse(localStorage.getItem('globalBgSettings'))
        if (settings) {
          // 确保背景图索引正确加载
          this.selectedBgIndex = settings.bgIndex !== undefined ? settings.bgIndex : -1
          
          // 透明度设置，确保正确处理0值
          this.contentTransparency = settings.contentTransparency !== undefined ? 
                                     settings.contentTransparency : 0.2
          this.bgTransparency = settings.bgTransparency !== undefined ? 
                               settings.bgTransparency : 1
        }
      } catch (e) {
        console.error('加载背景设置失败:', e)
      }
    },
    
    // 获取文章详情
    async getDetail(articleCode) {
      try {
        const response = await this.$http.get(`/api/articles/${articleCode}`)
        
        if (response.code === 200) {
          this.article = response.data
          
          // 直接使用marked渲染markdown内容
          this.renderMarkdown(this.article.article_info)
          
          // 确保导航显示
          this.showNavigation = true
          
          // 文章加载后重新应用背景设置
          this.$nextTick(() => {
            // 重新从localStorage加载设置，确保使用最新值
            this.loadBgSettings();
            // 绑定自定义容器点击事件
            const container = this.$refs.markdownDisplay
            if (container) {
              container.querySelectorAll('.container-header').forEach(header => {
                header.onclick = () => {
                  const box = header.closest('.custom-container')
                  if (!box) return
                  box.classList.toggle('collapsed')
                }
              })
            }
          });
        } else {
          this.$message.error(response.message || '获取文章详情失败')
        }
      } catch (error) {
        console.error('获取文章详情失败：', error)
        this.$message.error(error.message || '获取文章详情失败')
      }
    },

    // 增强数学公式中的颜色
    enhanceMathColors() {
      try {
        console.log('增强数学公式颜色');
        
        // 检查页面中是否有MathJax元素
        const mathJaxElements = document.querySelectorAll('.MathJax_SVG, .MathJax');
        console.log(`发现${mathJaxElements.length}个MathJax元素`);
        
        // 处理特殊的math-tex类
        const mathTexElements = document.querySelectorAll('.math-tex');
        mathTexElements.forEach(el => {
          // 如果已经处理过，跳过
          if (el.dataset.processed) return;
          
          const parentStyle = el.parentElement.style.color;
          if (parentStyle) {
            // 找到包含此元素的MathJax元素并应用颜色
            const mathJaxEl = el.closest('.MathJax_SVG, .MathJax');
            if (mathJaxEl) {
              const mathElems = mathJaxEl.querySelectorAll('.mi, .mo, .mn');
              mathElems.forEach(mathElem => {
                mathElem.style.color = parentStyle;
              });
            }
          }
          
          // 标记为已处理
          el.dataset.processed = 'true';
        });
        
        // 检查所有双美元符号公式
        const displayMathElements = document.querySelectorAll('.MathJax_SVG_Display, .MathJax_Display');
        console.log(`发现${displayMathElements.length}个行间公式`);
        
        // 确保行间公式正确渲染
        displayMathElements.forEach(container => {
          // 确保显示为块级元素
          container.style.display = 'block';
          container.style.textAlign = 'center';
          container.style.width = '100%';
          container.style.margin = '1em 0';
          
          // 处理容器内的公式
          const mathElement = container.querySelector('.MathJax, .MathJax_SVG');
          if (mathElement) {
            // 确保公式居中
            mathElement.style.margin = '0 auto';
            
            // 检查是否包含颜色样式
            if (mathElement.getAttribute('style') && 
                mathElement.getAttribute('style').includes('color:')) {
              console.log('发现彩色公式，应用特殊处理');
              container.dataset.colorFormula = 'true';
            }
          }
          
          // 确保容器是块级元素
          const parent = container.parentElement;
          if (parent && parent.style) {
            // 检查父元素是否为行内元素
            const computedStyle = window.getComputedStyle(parent);
            if (computedStyle.display === 'inline' || computedStyle.display === 'inline-block') {
              // 将父元素改为块级元素
              parent.style.display = 'block';
              parent.style.textAlign = 'center';
              parent.style.width = '100%';
              // 检查是否在列表项内，如果是则不设置margin
              const isInListItem = parent.closest('li');
              if (!isInListItem) {
              parent.style.margin = '1em 0';
              }
            }
          }
        });
        
        // 直接用DOM操作修复颜色显示问题
        document.querySelectorAll('.mjx-math [style*="color:"]').forEach(el => {
          console.log('发现带颜色的数学元素:', el.outerHTML.substring(0, 50));
        });
        
        // 添加点击放大功能
        this.addZoomCapability();
        
        // 给所有双美元符号的公式添加额外的样式强制
        setTimeout(() => {
          const displayMathContainers = document.querySelectorAll('.MathJax_SVG_Display, .MathJax_Display');
          displayMathContainers.forEach(container => {
            if (!container.classList.contains('enhanced')) {
              container.classList.add('enhanced');
              container.style.display = 'block';
              container.style.textAlign = 'center';
              container.style.margin = '1em 0';
              container.style.width = '100%';
              
              // 强制父元素为块级
              let parent = container.parentElement;
              if (parent && parent.tagName.toLowerCase() !== 'div' && 
                  parent.tagName.toLowerCase() !== 'p') {
                // 创建一个新的div元素
                const wrapper = document.createElement('div');
                wrapper.style.display = 'block';
                wrapper.style.width = '100%';
                wrapper.style.textAlign = 'center';
              // 检查是否在列表项内，如果是则不设置margin
              const isInListItem = parent.closest('li');
              if (!isInListItem) {
                wrapper.style.margin = '1em 0';
              }
                
                // 替换原来的元素
                parent.insertBefore(wrapper, container);
                wrapper.appendChild(container);
              }
            }
          });
        }, 1000);
      } catch (e) {
        console.error('增强数学颜色时出错:', e);
      }
    },
    
    // 添加公式点击放大功能
    addZoomCapability() {
      try {
        // 绑定处理函数，确保this上下文正确
        const handleMathClick = this.handleMathClick.bind(this);
        
        // 行内公式 - 这些已经是精确的公式元素
        const inlineMath = document.querySelectorAll('.MathJax, .MathJax_SVG');
        inlineMath.forEach(math => {
          if (!math.dataset.zoomable) {
            math.dataset.zoomable = "true";
            math.style.cursor = "pointer";
            math.addEventListener('click', handleMathClick);
          }
        });
        
        // 行间公式 - 特别处理，只为公式本身添加事件
        const displayMathContainers = document.querySelectorAll('.MathJax_Display, .MathJax_SVG_Display');
        displayMathContainers.forEach(container => {
          // 找到容器内的实际公式元素
          const actualMath = container.querySelector('.MathJax, .MathJax_SVG');
          if (actualMath && !actualMath.dataset.zoomable) {
            actualMath.dataset.zoomable = "true";
            actualMath.style.cursor = "pointer";
            
            // 只给实际公式元素添加点击事件，而不是整个容器
            actualMath.addEventListener('click', handleMathClick);
            
            // 防止事件冒泡到容器
            container.addEventListener('click', (e) => {
              // 如果点击的不是公式本身，阻止事件冒泡
              if (e.target !== actualMath && !actualMath.contains(e.target)) {
                e.stopPropagation();
              }
            });
          }
        });
        
        console.log('添加了点击放大功能');
      } catch (e) {
        console.error('添加点击放大功能失败:', e);
      }
    },
    
    // 处理公式点击事件
    handleMathClick(event) {
      try {
        // 阻止事件传播
        event.stopPropagation();
        event.preventDefault();
        
        // 获取点击的MathJax元素
        const mathElement = event.currentTarget;
        
        // 检查是否已经存在放大层
        if (document.querySelector('.custom-math-zoom')) {
          return;
        }
        
        // 获取原公式的位置和尺寸
        const mathRect = mathElement.getBoundingClientRect();
        
        // 创建放大后的容器
        const zoomContainer = document.createElement('div');
        zoomContainer.className = 'custom-math-zoom';
        zoomContainer.style.position = 'absolute';
        zoomContainer.style.zIndex = '9999';
        zoomContainer.style.backgroundColor = 'white';
        zoomContainer.style.padding = '15px';
        zoomContainer.style.borderRadius = '8px';
        zoomContainer.style.boxShadow = '0 0 20px rgba(0,0,0,0.2)';
        zoomContainer.style.overflow = 'auto';
        zoomContainer.style.border = '1px solid #ddd';
        
        // 检测暗色模式
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
          zoomContainer.style.backgroundColor = '#1e1e1e';
          zoomContainer.style.color = '#e0e0e0';
          zoomContainer.style.borderColor = '#444';
        }
        
        // 复制公式内容
        const mathContent = mathElement.cloneNode(true);
        
        // 处理大小，增加缩放效果
        const mathJaxElement = mathContent.querySelector('.MathJax, .MathJax_SVG');
        if (mathJaxElement) {
          mathJaxElement.style.transform = 'scale(1.5)';
          mathJaxElement.style.transformOrigin = 'center';
          mathJaxElement.style.display = 'block';
          mathJaxElement.style.margin = '0 auto';
        }
        
        // 添加关闭按钮
        const closeButton = document.createElement('div');
        closeButton.className = 'math-zoom-close';
        closeButton.innerHTML = '×';
        closeButton.style.position = 'absolute';
        closeButton.style.top = '5px';
        closeButton.style.right = '5px';
        closeButton.style.cursor = 'pointer';
        closeButton.style.fontSize = '20px';
        closeButton.style.fontWeight = 'bold';
        closeButton.style.color = '#666';
        closeButton.style.width = '24px';
        closeButton.style.height = '24px';
        closeButton.style.lineHeight = '24px';
        closeButton.style.textAlign = 'center';
        closeButton.style.borderRadius = '50%';
        closeButton.style.background = 'rgba(0,0,0,0.05)';
        
        // 暗色模式下的关闭按钮样式
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
          closeButton.style.color = '#aaa';
          closeButton.style.background = 'rgba(255,255,255,0.1)';
        }
        
        closeButton.addEventListener('click', (e) => {
          e.stopPropagation();
          document.body.removeChild(overlay);
          document.body.removeChild(zoomContainer);
        });
        
        // 创建半透明遮罩背景
        const overlay = document.createElement('div');
        overlay.style.position = 'fixed';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.right = '0';
        overlay.style.bottom = '0';
        overlay.style.backgroundColor = 'rgba(0,0,0,0.3)';
        overlay.style.zIndex = '9998';
        
        // 点击遮罩关闭
        overlay.addEventListener('click', () => {
          document.body.removeChild(overlay);
          document.body.removeChild(zoomContainer);
        });
        
        // 组装弹窗
        zoomContainer.appendChild(mathContent);
        zoomContainer.appendChild(closeButton);
        
        // 先添加到DOM以获取尺寸
        document.body.appendChild(zoomContainer);
        
        // 动态调整宽度 - 确保宽度合适
        const minWidth = Math.max(mathRect.width * 1.2, 300);
        const maxWidth = Math.min(window.innerWidth * 0.9, 800);
        const contentWidth = zoomContainer.scrollWidth;
        
        // 设置最终宽度 - 在最小和最大宽度之间取一个合适的值
        const finalWidth = Math.min(Math.max(contentWidth, minWidth), maxWidth);
        zoomContainer.style.width = `${finalWidth}px`;
        
        // 最大高度，避免超出屏幕
        zoomContainer.style.maxHeight = `${window.innerHeight * 0.7}px`;
        
        // 获取调整后的尺寸
        const zoomRect = zoomContainer.getBoundingClientRect();
        
        // 计算位置 - 在原公式上方居中
        let topPosition = mathRect.top - zoomRect.height - 10;
        
        // 如果放不下，就放在下方
        if (topPosition < 10) {
          topPosition = mathRect.bottom + 10;
        }
        
        // 如果下方也放不下，就放在视口中间
        if (topPosition + zoomRect.height > window.innerHeight - 10) {
          topPosition = Math.max(10, (window.innerHeight - zoomRect.height) / 2);
        }
        
        // 水平居中
        const leftPosition = Math.max(10, mathRect.left + (mathRect.width - zoomRect.width) / 2);
        
        // 确保不超出右侧边界
        if (leftPosition + zoomRect.width > window.innerWidth - 10) {
          zoomContainer.style.right = '10px';
        } else {
          zoomContainer.style.left = `${leftPosition}px`;
        }
        
        zoomContainer.style.top = `${topPosition}px`;
        
        // 添加到页面
        document.body.appendChild(overlay);
        // zoomContainer已经添加过了，不需要再次添加
        
        console.log('显示放大的公式在原位置上方');
      } catch (e) {
        console.error('公式放大处理失败:', e);
      }
    },
    
    // 返回按钮点击事件
    goBack() {
      // 直接跳转到文章列表页
      this.$router.push('/article')
    }
  }
}
</script>

<style lang="scss">
/* 全局样式 - 非scoped */
.markdown-display blockquote.custom-blockquote {
  margin: 20px 0 !important;
  padding: 15px 20px 15px 35px !important;
  color: #476582 !important;
  background-color: rgba(235, 245, 255, 0.7) !important;
  border-left: 4px solid #409EFF !important;
  border-radius: 0 4px 4px 0 !important;
  position: relative !important;
  display: block !important;
  box-sizing: border-box !important;
  
  /* 引号图标 */
  .quote-icon {
    position: absolute !important;
    left: 10px !important;
    top: 8px !important;
    font-size: 20px !important;
    color: #409EFF !important;
    opacity: 0.5 !important;
  }
  
  /* 确保段落样式正确 */
  p {
    margin: 8px 0 !important;
    line-height: 1.6 !important;
    color: #476582 !important;
  }
  
  /* 嵌套引用样式 */
  blockquote.custom-blockquote {
    margin: 10px 0 !important;
    background-color: rgba(235, 245, 255, 0.5) !important;
    border-left-color: #7babff !important;
  }
}

/* 暗色模式引用样式 */
@media (prefers-color-scheme: dark) {
  .markdown-display blockquote.custom-blockquote {
    background-color: rgba(45, 55, 72, 0.7) !important;
    color: #a3b9d0 !important;
    border-left-color: #5c84a9 !important;
    
    .quote-icon {
      color: #5c84a9 !important;
    }
    
    p {
      color: #a3b9d0 !important;
    }
    
    blockquote.custom-blockquote {
      background-color: rgba(45, 55, 72, 0.5) !important;
      border-left-color: #4a6b8a !important;
    }
  }
}

/* 放大显示的公式样式 - 修复遮盖问题 */
.MathJax_Zoom {
  position: fixed !important;
  z-index: 9999 !important;
  background-color: white !important;
  padding: 10px !important;
  border-radius: 4px !important;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.2) !important;
  max-width: 90% !important;
  margin: 0 auto !important;
  left: 0 !important;
  right: 0 !important;
  top: 50px !important;
  overflow: auto !important;
  max-height: 80vh !important;
}

/* 暗色模式下放大公式样式 */
@media (prefers-color-scheme: dark) {
  .MathJax_Zoom {
    background-color: #1e1e1e !important;
    color: #e0e0e0 !important;
  }
}

/* 行内公式样式 */
.markdown-display .MathJax:not(.MathJax_Display),
.markdown-display .MathJax_SVG:not(.MathJax_SVG_Display),
.markdown-display .mjx-chtml:not(.MJXc-display) {
  margin: 0 0.15em !important;
  color: #0550ae !important;
  vertical-align: middle !important;
  display: inline-block !important;
}
</style>

<style lang="scss" scoped>
.article-view {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
  background-color: #f0f2f5;
  overflow: hidden;
  position: relative; /* 添加相对定位 */

  /* 背景图层 */
  .background-layer {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    pointer-events: none; /* 确保不会干扰鼠标事件 */
    z-index: 0; /* 放在最底层 */
    width: 100vw;
    height: 100vh;
  }

  /* 文章头部 */
  .article-header {
    margin: 8px 16px 0 16px;
    flex-shrink: 0;
    position: relative;
    z-index: 1; /* 确保在背景层之上 */
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12); /* 增强阴影效果 */
    
    /* 修改卡片内部背景为透明 */
    :deep(.el-card__body) {
      padding: 20px 20px 12px; /* 减小底部内边距从20px到12px */
      background-color: transparent;
    }

    /* 头部按钮组 */
    .header-buttons {
      position: absolute;
      bottom: 10px;
      right: 10px;
      display: flex;
      gap: 10px;
      z-index: 2;
    }

    /* 图标按钮通用样式 */
    .icon-btn {
      background-color: #409eff;
      color: #ffffff;
      border-color: #409eff;
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
      transform: translateY(0);
      transition: all 0.3s ease;
      
      i {
        font-size: 18px;
        margin: 0;
        line-height: 1;
      }
      
      &:hover, &:focus {
        background-color: #66b1ff;
        color: #ffffff;
        border-color: #66b1ff;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.25);
        transform: translateY(-2px);
      }
      
      &:active {
        background-color: #3a8ee6;
        color: #ffffff;
        border-color: #3a8ee6;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
        transform: translateY(1px);
      }
    }

    .header-content {
      .title-row {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;
        flex-wrap: wrap; /* 在小屏幕上允许换行 */
        
        .article-type {
          flex-shrink: 0;
          font-size: 13px;
          padding: 4px 12px;
          border-radius: 4px;
          min-width: 60px;
          text-align: center;
          display: inline-flex;
          justify-content: center;
          align-items: center;
          height: 26px;
          line-height: 1;
          margin-right: 6px; /* 添加右边距 */
          font-weight: 600; /* 增加字体粗细 */
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* 添加轻微阴影 */
        }
        
        .article-title {
          font-size: 32px;
          font-weight: 600;
          color: #1f2937;
          margin: 0; /* 移除底部边距，因为已经在title-row中设置了margin-bottom */
          line-height: 1.4;
          text-shadow: 0 1px 2px rgba(255, 255, 255, 0.5); /* 添加文字阴影提高可读性 */
        }
      }

      .time-info {
        font-size: 13px;
        color: #606266; /* 加深颜色，从#909399改为#606266 */
        display: flex;
        align-items: center;
        gap: 8px; /* 减小间距从24px到8px，因为现在有分隔符 */
        margin-bottom: 0; /* 添加底部边距为0 */
        font-weight: 500; /* 增加字体粗细 */

        span {
          display: flex;
          align-items: center;
          gap: 4px;
          
          &::before {
            display: none; /* 移除原来的点状分隔符 */
          }
          
          i {
            margin-right: 4px;
            font-size: 14px;
          }
        }
        
        .divider {
          color: #909399; /* 保持分隔符颜色 */
          margin: 0 4px;
        }
        
        .article-tags {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;

          .article-tag {
            font-size: 12px;
            padding: 0 8px;
            height: 24px;
            line-height: 22px;
            border-radius: 4px;
            background-color: #ecf5ff;
            color: #409eff;
            border: 1px solid #d9ecff;
            font-weight: 500; /* 增加字体粗细 */
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); /* 添加轻微阴影 */

            &:hover {
              background-color: #409eff;
              border-color: #409eff;
              color: #fff;
            }
          }
        }
      }
    }
  }

  /* 内容区域容器 */
  .content-container {
    flex: 1;
    min-height: 0;
    position: relative;
    z-index: 1; /* 确保在背景层之上 */
    
    /* 主内容包装器 */
    .main-content-wrapper {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      margin: 8px 16px 16px;
      display: flex;
      
      /* 文章内容区域 */
      .article-content {
        flex: 1;
        min-width: 0;
        margin-right: 8px;
        transition: margin-right 0.3s ease, background-color 0.3s ease;
        /* 移除背景色，将由计算属性控制 */
        
        &.no-navigation {
          margin-right: 0;
        }
        
        :deep(.el-card__body) {
          padding: 0;
          height: 100%;
          background-color: transparent;
        }
        
        /* 自定义Markdown容器 */
        .custom-markdown-container {
          height: 100%;
          overflow: hidden;
          position: relative;
          
          .markdown-display {
            height: 100%;
            overflow-y: auto;
            padding: 20px;
            box-sizing: border-box;
            font-size: 15px;
            line-height: 1.75;
            color: #2c3e50;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
            
            /* 标题样式 */
            h1, h2, h3, h4, h5, h6 {
              margin-top: 24px;
              margin-bottom: 16px;
              font-weight: 600;
              line-height: 1.25;
              color: #273849;
            }
            
            h1 {
              font-size: 2em;
              padding-bottom: 0.3em;
              border-bottom: 1px solid #eaecef;
            }
            
            h2 {
              font-size: 1.5em;
              padding-bottom: 0.3em;
              border-bottom: 1px solid #eaecef;
            }
            
            h3 {
              font-size: 1.25em;
            }
            
            h4 {
              font-size: 1em;
            }
            
            /* 段落样式 */
            p {
              margin-bottom: 16px;
              line-height: 1.7;
            }
            
            /* 链接样式 */
            a {
              color: #0366d6;
              text-decoration: none;
              
              &:hover {
                text-decoration: underline;
              }
            }
            
            /* 代码块样式 */
            pre {
              margin: 16px 0;
              border-radius: 6px;
              overflow: hidden;
              position: relative;
              width: 90%;
              max-width: 900px;
              background-color: rgba(13, 17, 23, 0.9); /* 添加透明度，从纯黑改为半透明深色 */
              border: 1px solid #30363d;
              display: flex;
              flex-direction: column;
              
              .code-container {
                flex: 1;
                overflow-x: auto;
                position: relative;
              }
              
              code {
                padding: 16px;
                margin-left: 40px; /* 为行号预留空间，让代码靠左显示 */
                display: block;
                box-sizing: border-box;
              }
              
              .line-numbers {
                position: absolute;
                left: 0;
                top: 0;
                bottom: 0;
                padding: 16px 8px;
                color: #6e7681;
                text-align: right;
                background-color: #161b22;
                border-right: 1px solid #30363d;
                user-select: none;
                font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
                white-space: pre;
                line-height: 1.5;
                font-size: 14px;
                width: 40px; /* 固定宽度 */
                box-sizing: border-box;
                z-index: 1;
              }
              
              .copy-code-btn {
                position: absolute;
                top: 8px;
                right: 8px;
                padding: 4px 8px;
                background: rgba(110, 118, 129, 0.4);
                color: #c9d1d9;
                border: none;
                border-radius: 4px;
                font-size: 12px;
                cursor: pointer;
                opacity: 0;
                transition: opacity 0.2s;
                z-index: 10;
              }
              
              &:hover .copy-code-btn {
                opacity: 1;
              }
              
              .copy-code-btn:hover {
                background: rgba(110, 118, 129, 0.6);
              }
            }
            
            /* 列表样式 */
            ul, ol {
              padding-left: 2em;
              margin-top: 0 !important;
              margin-bottom: 0 !important;
              margin-block-start: 0 !important;
              margin-block-end: 0 !important;
              
              li {
                /* 移除margin-bottom，避免列表项间距过大 */
                
                p {
                  margin: 0;
                }
              }
            }
            
            /* 任务列表样式 - 隐藏列表标记，只显示复选框 */
            ul.task-list {
              list-style: none !important;
              padding-left: 20px !important; /* 任务列表左缩进 */
              
              li.task-list-item {
                list-style: none !important;
                /* 移除margin-bottom，避免列表项间距过大 */
                display: flex;
                flex-wrap: wrap; /* 允许子元素换行 */
                /* 二级列表需要换行显示，同时首行复选框与文字居中对齐 */
                align-items: center;
                
                /* 隐藏默认的列表标记 */
                &::before {
                  display: none !important;
                }
                
                /* 任务列表复选框样式 */
                input[type="checkbox"] {
                  margin: 0 8px 0 0;
                  flex-shrink: 0;
                  transform: scale(1);
                  /* 确保复选框与文本基线对齐 */
                  vertical-align: middle;
                  position: relative;
                  top: -1px; /* 微调位置，确保完美对齐 */
                }
                
                /* 任务列表文本内容 */
                > span, > p {
                  margin: 0;
                  flex: 1;
                  /* 确保文本与复选框基线对齐 */
                  line-height: 1.5;
                  display: inline-block;
                  vertical-align: middle;
                }

                /* 嵌套列表应换行并缩进显示 */
                > ul, > ol {
                  /* 让子列表占据整行，避免与上行同一行 */
                  flex-basis: 100%;
                  width: 100%;
                  display: block;
                  margin: 4px 0 0 0;
                }
                > ul { padding-left: 1.5em; }
                > ol { padding-left: 1.5em; }
              }
            }
            
            /* 普通无序/有序列表：PC 左缩进 40px，保持正常上下间距 */
            .markdown-display ul,
            .markdown-display ol {
              padding-left: 40px !important;
            }
            /* 非任务UL强制保持40px左缩进 */
            .markdown-display ul:not(.task-list):not(:has(li input[type="checkbox"])) {
              padding-left: 40px !important;
            }

            /* 连续列表之间的顶部间距清零 */
            .markdown-display ul + ul,
            .markdown-display ul + ol,
            .markdown-display ol + ul,
            .markdown-display ol + ol {
              margin-top: 0 !important;
            }
            /* 混合列表：仅普通项显示小黑点 */
            .markdown-display ul:has(li input[type="checkbox"]) > li:not(.task-list-item) {
              list-style: disc !important;
            }
            .markdown-display ul.task-list > li:not(.task-list-item) {
              list-style: disc !important;
            }
            
            /* 折叠与列表相邻段落的外边距 */
            .markdown-display p:has(+ ul),
            .markdown-display p:has(+ ol),
            .markdown-display p:has(+ ul.task-list) {
              margin-top: 0 !important;
              margin-bottom: 0 !important;
            }
            .markdown-display ul + p,
            .markdown-display ol + p {
              margin-top: 0 !important;
            }
            
            /* 强制处理列表项内部段落的margin - 只处理p标签，不处理li本身 */
            .markdown-display ul:not(.task-list) li > p,
            .markdown-display ol li > p {
              margin-top: 0 !important;
              margin-bottom: 0 !important;
              margin-block-start: 0 !important;
              margin-block-end: 0 !important;
            }
            
            /* 更强制性的规则 - 确保所有列表项内的p标签margin被清零 */
            .markdown-display ul li p,
            .markdown-display ol li p {
              margin-top: 0 !important;
              margin-bottom: 0 !important;
              margin-block-start: 0 !important;
              margin-block-end: 0 !important;
            }
            
            /* 最高优先级规则 - 强制覆盖浏览器默认样式 */
            .markdown-display ul li > p,
            .markdown-display ol li > p {
              margin: 0 !important;
              margin-top: 0 !important;
              margin-bottom: 0 !important;
              margin-block-start: 0 !important;
              margin-block-end: 0 !important;
              margin-inline-start: 0 !important;
              margin-inline-end: 0 !important;
            }
            
            /* 专门针对任务列表后无序列表的p标签 - 最高优先级强制清零 */
            .markdown-display ul:not(.task-list) li > p {
              margin: 0 !important;
              margin-top: 0 !important;
              margin-bottom: 0 !important;
              margin-block-start: 0 !important;
              margin-block-end: 0 !important;
              margin-inline-start: 0 !important;
              margin-inline-end: 0 !important;
            }
            
            /* 空段落清零外边距，避免空行导致的1em间距 */
            .markdown-display p:empty {
              margin-top: 0 !important;
              margin-bottom: 0 !important;
              margin-block-start: 0 !important;
              margin-block-end: 0 !important;
            }

            /* 图片最大宽度不超出内容区域，高度按比例自适应 */
            :deep(img) {
              max-width: 100% !important;
              height: auto !important;
              display: block !important;
            }
            
            /* 仅包含空白字符或换行的段落也清零 */
            .markdown-display p:has(br:only-child) {
              margin-top: 0 !important;
              margin-bottom: 0 !important;
              margin-block-start: 0 !important;
              margin-block-end: 0 !important;
            }
            
            /* 兼容性：处理没有task-list类的任务列表 */
            ul li input[type="checkbox"] {
              margin: 0 8px 0 0;
              transform: scale(1);
              vertical-align: middle;
              position: relative;
              top: -1px;
            }
            
            /* 针对包含复选框的列表项，隐藏列表标记 - 使用更兼容的选择器 */
            ul li input[type="checkbox"] {
              margin: 0 8px 0 0;
              transform: scale(1);
              vertical-align: middle;
              position: relative;
              top: -1px;
            }
            
            /* 通过JavaScript动态添加样式类来处理任务列表项 */
            ul li.task-list-item {
              list-style: none;
              display: flex;
              align-items: center; /* 改为center确保垂直居中对齐 */
              
              &::before {
                display: none;
              }
            }
            
            /* 确保包含复选框的列表项都有正确的样式 */
            ul li:has(input[type="checkbox"]) {
              list-style: none;
              display: flex;
              align-items: center;
              
              &::before {
                display: none;
              }
            }
            
            /* 强制任务列表缩进 - 最高优先级 */
            ul:has(li input[type="checkbox"]) {
              padding-left: 20px !important;
              list-style: none !important;
            }
            
            /* 备用方案：通过属性选择器 */
            ul[class*="task"] {
              padding-left: 20px !important;
              list-style: none !important;
            }
          }
        }
      }
      
      /* 导航卡片 */
      .navigation-card {
        width: 250px;
        flex-shrink: 0;
        /* 移除背景色，将由计算属性控制 */
        border-radius: 4px;
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
        display: flex;
        flex-direction: column;
        transition: width 0.3s ease, opacity 0.3s ease, background-color 0.3s ease;
  
        .navigation-header {
          padding: 10px 15px;
          border-bottom: 1px solid #ebeef5;
          background-color: rgba(255, 255, 255, 0.8);
          display: flex;
          justify-content: space-between;
          align-items: center;
          
          .title-wrapper {
            position: relative;
            cursor: pointer;
            
            span {
              font-size: 16px;
              font-weight: 500;
              color: #303133;
            }
            
            .custom-tooltip {
              position: absolute;
              top: -35px;
              left: 50%;
              transform: translateX(-50%);
              background-color: #303133;
              color: white;
              padding: 5px 12px;
              border-radius: 4px;
              font-size: 12px;
              white-space: nowrap;
              z-index: 9999;
              
              &::after {
                content: '';
                position: absolute;
                bottom: -5px;
                left: 50%;
                transform: translateX(-50%);
                width: 0;
                height: 0;
                border-left: 5px solid transparent;
                border-right: 5px solid transparent;
                border-top: 5px solid #303133;
              }
            }
          }
          
          i {
            font-size: 18px;
            color: #909399;
            cursor: pointer;
            
            &:hover {
              color: #409eff;
            }
          }
        }
        
        .navigation-content {
          flex: 1;
          overflow-y: auto;
          padding: 10px 0;
          
          .navigation-item {
            padding: 8px 10px 8px 0;
            cursor: pointer;
            font-size: 14px;
            color: #606266;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            
            &:hover {
              color: #409eff;
              background-color: #f5f7fa;
            }
            
            &.active {
              color: #409eff;
              font-weight: 500;
              background-color: #ecf5ff;
              border-right: 3px solid #409eff;
            }
          }
          
          .navigation-empty {
            padding: 20px;
            text-align: center;
            color: #909399;
          }
        }
      }
    }
  }
}

/* 文章类型标签 */
.article-type {
  border: none;
}

/* 背景设置对话框样式 */
:deep(.bg-settings-dialog) {
  /* 这部分样式将由BackgroundSettings组件提供 */
}
</style> 